import * as XLSX from 'xlsx';

const isCell = (key) => /^[A-Z]+\d+$/.test(key);

const parseCell = (cell) => {
  const match = cell.match(/^(?<column>[A-Z]+)(?<row>\d+)$/);
  if (!match) return [0, 'A'];
  const { column, row } = match.groups;
  return [parseInt(row, 10) - 1, column];
};

export function parseSheet(sheet, mappers) {
  const warnings = [];

  // If sheet is already an array of objects with header keys, just map mappers
  if (Array.isArray(sheet) && typeof sheet[0] === 'object' && !Array.isArray(sheet[0])) {
    const normalizeKey = (key) => key && key.toString().trim().toLowerCase().replace(/\s+/g, '_')
      .replace(/_+/g, '_');
    const normalizedMappers = Object.fromEntries(
      Object.entries(mappers).map(([k, v]) => [normalizeKey(k), v]),
    );
    const rows = sheet.map((row) => {
      const mapped = {};
      Object.entries(row).forEach(([key, value]) => {
        const normKey = normalizeKey(key);
        const mapper = normalizedMappers[normKey];
        
        if (mapper && typeof mapper === 'function') {
          mapped[normKey] = mapper(value);
        } else {
          mapped[normKey] = value;
        }
      });
      return mapped;
    });
    return { rows, warnings };
  }

  // Helper to normalize header keys: lowercase, remove spaces and underscores
  const normalizeKey = (key) => key && key
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');

  const result = Object.keys(sheet)
    .filter(isCell)
    .reduce((acc, cell) => {
      const [rowIndex, columnIndex] = parseCell(cell);
      const { v: value } = sheet[cell];

      if (rowIndex === 0) {
        // Only add columns with valid, non-empty headers
        if (value && value.toString().trim() !== '') {
          acc.columns.push({ column: columnIndex, value: value.toString().trim() });
        } else {
          // Log untitled columns for debugging
          const warning = `Skipping untitled column at position ${columnIndex} (value: "${value}")`;
          console.warn(warning);
          warnings.push(warning);
        }
      } else {
        // Normalize all header keys for matching
        const normalizedColumns = acc.columns.map((col) => ({ ...col, norm: normalizeKey(col.value) }));
        const field = normalizedColumns.find((col) => col.column === columnIndex);
        const currentRow = acc.rows[rowIndex - 1] || {};

        // Skip processing if field.value is undefined, null, or empty (untitled helper columns)
        if (!field || !field.value || field.value.toString().trim() === '') {
          return acc;
        }

        // Normalize mappers keys for matching
        const normalizedMappers = Object.fromEntries(
          Object.entries(mappers).map(([k, v]) => [normalizeKey(k), v]),
        );
        const normKey = field.norm;
        const mapper = normalizedMappers[normKey];
        if (mapper && typeof mapper === 'function') {
          // If mapper accepts 2 parameters, pass the row as second parameter
          if (mapper.length > 1) {
            currentRow[normKey] = mapper(value, currentRow);
          } else {
            currentRow[normKey] = mapper(value);
          }
        } else if (normKey in normalizedMappers) {
          currentRow[normKey] = normalizedMappers[normKey](value);
        } else {
          currentRow[normKey] = value;
        }

        acc.rows[rowIndex - 1] = currentRow;
      }

      return acc;
    }, { columns: [], rows: [] });

  return { ...result, warnings };
}

export function combine({
  events, sessions, modules, options,
}) {
  // Extract rows and warnings from each sheet
  const eventsData = Array.isArray(events) ? { rows: events, warnings: [] } : events;
  const sessionsData = Array.isArray(sessions) ? { rows: sessions, warnings: [] } : sessions;
  const modulesData = Array.isArray(modules) ? { rows: modules, warnings: [] } : modules;
  const optionsData = Array.isArray(options) ? { rows: options, warnings: [] } : options;

  // Collect all warnings
  const allWarnings = [
    ...eventsData.warnings,
    ...sessionsData.warnings,
    ...modulesData.warnings,
    ...optionsData.warnings,
  ];

  optionsData.rows.forEach((option) => {
    const module = modulesData.rows.find((m) => m['#'] === option.module);
    if (!module) return;

    module.options = [
      ...(module.options || []),
      option,
    ];
  });

  modulesData.rows.forEach((module) => {
    const session = sessionsData.rows.find((s) => s['#'] === module.session);
    if (!session) return;

    session.modules = [
      ...(session.modules || []),
      module,
    ];
  });

  sessionsData.rows.forEach((session) => {
    const event = eventsData.rows.find((e) => e['#'] === session.event);
    if (!event) return;

    event.sessions = [
      ...(event.sessions || []),
      session,
    ];
  });

  return { events: eventsData.rows, warnings: allWarnings };
}

export const Mappers = {
  toNum: (value) => +value,
  Boolean: (value) => {
    if (!value) return false;
    const upperValue = value.toString().toUpperCase().trim();
    if (upperValue === 'Y' || upperValue === 'YES' || upperValue === 'TRUE' || upperValue === '1') {
      return true;
    }
    if (upperValue === 'N' || upperValue === 'NO' || upperValue === 'FALSE' || upperValue === '0') {
      return false;
    }
    // For any other value, default to false (safer than defaulting to true)
    return false;
  },
  toString: (value) => value.toString(),
  toStringAndCheckNull: (value) => (value ? value.toString() : 'banner'),
  toStringWithoutHash: (value) => value.toString().replace('#', ''),
  toUpperCase: (value) => value.toUpperCase(),
  trim: (value) => (value ? value.toString().trim() : value),
  // Date fields are now parsed by the API, not here.
};

// Normalizes a sheet from [headerRow, {0:..., 1:...}, ...] to [{header: value, ...}, ...]
function normalizeSheetRows(sheet) {
  if (!Array.isArray(sheet) || sheet.length < 2) return [];
  const headers = sheet[0];
  return sheet.slice(1).map((rowObj) => {
    const row = {};
    headers.forEach((header, i) => {
      // Some APIs return rowObj as an array, some as an object with numeric keys
      const value = rowObj[i] !== undefined ? rowObj[i] : rowObj[String(i)];
      row[header] = value;
    });
    return row;
  });
}

// Parser functions for different sheet types
export function parseEvents(sheets) {
  let eventsSheet = sheets.Events || sheets.events;
  if (!eventsSheet) return { rows: [], warnings: [] };

  // Normalize if needed
  if (Array.isArray(eventsSheet) && Array.isArray(eventsSheet[0])) {
    eventsSheet = normalizeSheetRows(eventsSheet);
  }

  const mappers = {
    '#': Mappers.toNum,
    name: Mappers.trim,
    description: Mappers.trim,
    logo: Mappers.trim,
    domain: Mappers.trim,
    primary_color: Mappers.trim,
    secondary_color: Mappers.trim,
    display_header: Mappers.Boolean,
    display_qr: Mappers.Boolean,
    display_menu: Mappers.Boolean,
    // Dates are parsed by the API
    start_date: (value) => value,
    end_date: (value) => value,
  };

  const result = parseSheet(eventsSheet, mappers);
  return { rows: result.rows, warnings: result.warnings };
}

export function parseSessions(sheets) {
  let sessionsSheet = sheets.Sessions || sheets.sessions;
  if (!sessionsSheet) return { rows: [], warnings: [] };

  if (Array.isArray(sessionsSheet) && Array.isArray(sessionsSheet[0])) {
    sessionsSheet = normalizeSheetRows(sessionsSheet);
  }

  const mappers = {
    '#': Mappers.toNum,
    name: Mappers.trim,
    description: Mappers.trim,
    event: Mappers.toNum,
    is_published: Mappers.Boolean,
    is_live: Mappers.Boolean,
    show_terms: Mappers.Boolean,
    image: Mappers.trim,
  };

  const result = parseSheet(sessionsSheet, mappers);
  return { rows: result.rows, warnings: result.warnings };
}

export function parseModules(sheets) {
  let modulesSheet = sheets.Modules || sheets.modules;
  if (!modulesSheet) return { rows: [], warnings: [] };

  if (Array.isArray(modulesSheet) && Array.isArray(modulesSheet[0])) {
    modulesSheet = normalizeSheetRows(modulesSheet);
  }

  const mappers = {
    '#': Mappers.toNum,
    session: Mappers.toNum,
    type: Mappers.trim,
    subtype: Mappers.trim,
    timezone: Mappers.trim,
    // Dates are parsed by the API
    schedule_start: (value) => value,
    schedule_end: (value) => value,
    title: Mappers.trim,
    subtitle: Mappers.trim,
    image: Mappers.trim,
    show_votes: Mappers.Boolean,
    show_votes_count: Mappers.Boolean,
    vote_count: Mappers.toNum, // Add vote_count field parsing
    vote_limit: Mappers.toNum,
    collect_contact_details: Mappers.Boolean,
    cta: Mappers.trim,
    hyperlink: Mappers.trim,
    show_comments: Mappers.Boolean,
    show_comments_moderated: Mappers.Boolean,
    status: Mappers.trim,
  };

  const result = parseSheet(modulesSheet, mappers);
  return { rows: result.rows, warnings: result.warnings };
}

export function parseOptions(sheets) {
  let optionsSheet = sheets.Options || sheets.options;
  if (!optionsSheet) return { rows: [], warnings: [] };

  if (Array.isArray(optionsSheet) && Array.isArray(optionsSheet[0])) {
    optionsSheet = normalizeSheetRows(optionsSheet);
  }

  const mappers = {
    module: Mappers.toNum,
    title: Mappers.trim,
    subtitle: Mappers.trim,
    image: Mappers.trim,
    url: Mappers.trim,
  };

  const result = parseSheet(optionsSheet, mappers);
  return { rows: result.rows, warnings: result.warnings };
}

export default function parseFile(file) {
  const workbook = XLSX.read(file, { type: 'binary' });
  const { Sheets } = workbook;

  const result = combine({
    events: parseEvents(Sheets),
    sessions: parseSessions(Sheets),
    modules: parseModules(Sheets),
    options: parseOptions(Sheets),
  });

  return result.events;
}

export function parseFileWithWarnings(file) {
  const workbook = XLSX.read(file, { type: 'binary' });
  const { Sheets } = workbook;

  const result = combine({
    events: parseEvents(Sheets),
    sessions: parseSessions(Sheets),
    modules: parseModules(Sheets),
    options: parseOptions(Sheets),
  });

  return result;
}
